import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { batch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { 
    Alert,
    Backdrop, 
    Box, 
    Button, 
    Card, 
    Checkbox, 
    CircularProgress, 
    FormControl, 
    FormControlLabel, 
    FormGroup, 
    FormHelperText, 
    InputLabel, 
    MenuItem, 
    Select, 
    Snackbar, 
    TextField, 
    Typography } from '@mui/material';
import QuestionBox from '../components/QuestionBox';
import DetailBox from '../components/DetailBox'; 
import ImageBox from '../components/ImageBox';
import SendIcon from '@mui/icons-material/Send';
import { useAppSelector, useAppDispatch } from '../app/hook';
import { selectAnswers, replaceAllAnswers } from '../features/answerList/answerListSlice';
import { selectErrors, replaceAllErrors, setValue, setErrorText } from '../features/errorList/errorListSlice';
import { selectContactInfo, update } from '../features/contactInfo/contactInfoSlice';
import NotFoundPage from '../components/NotFoundPage';
import Cookies from 'js-cookie';

const Form = () => {
    const { hash } = useParams();

    const loading = useRef(false);
    const copyrightColor = useRef("text-white");

    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [body, setBody] = useState<any[]>([]);
    const [requiredInfo, setRequiredInfo] = useState<any>({
        salutation: true,
        firstName: true,
        lastName: true,
        title: true,
        phoneNum: true,
        mobileNum: true,
        email: true,
        compName: true,
        compAddress: true,
        compSize: true,
        industry: true,
        platform: true,
        declaration: true,
    });
    const [bgColor, setBgColor] = useState("bg-gray-800");
    const [bgimage, setBgImage] = useState<string | ArrayBuffer | null>(null);
    const [hdimage, setHdImage] = useState<string | ArrayBuffer | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [finished, setFinished] = useState(false);
    const [errorAlert, setErrorAlert] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [score, setScore] = useState(0)

    const answerList = useAppSelector(selectAnswers);
    const errorList = useAppSelector(selectErrors);
    const contactInfo = useAppSelector(selectContactInfo);
    const dispatch = useAppDispatch();

    const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

    useEffect(() => {
        // check end user whether submit the form recently
        if(Cookies.get('submitted') !== undefined) {
            loading.current = true;
            setFinished(true);
            window.scrollTo(0, 0);
            return;
        }

        // fetch form content
        if(!loading.current) {
            axios.get(`${API_ENDPOINT}/getFormContent`, { params: {
                hash: hash
            }})
            .then((response) => {
                const formContent = response.data;
                const body = JSON.parse(formContent.body);
                const requiredInfoOld = JSON.parse(formContent.required_info);

                const initialAnswers: Record<string, any> = {};
                const initialErrors: Record<string, any> = {};

                for (const element of body) {
                    if (element.type === "question") {
                        if (element.variant === "multiple") {
                            const init = element.otherOption
                                ? Array.from({ length: element.options.length + 1 }, () => false)
                                : Array.from({ length: element.options.length }, () => false);
                            initialAnswers[element.id] = {
                                values: init,
                                required: element.required,
                                type: element.variant,
                            };
                        } else {
                            initialAnswers[element.id] = {
                                values: [],
                                required: element.required,
                                type: element.variant,
                            };
                        }
                        if (element.otherOption) {
                            initialAnswers[element.id].otherInfo = "";
                        }
                        initialErrors[element.id] = { value: false, errorText: "" };
                    }
                }

                for (const key in requiredInfo) {
                    initialErrors[key] = { value: false, errorText: "" };
                }

                batch(() => {
                    dispatch(replaceAllAnswers(initialAnswers));
                    dispatch(replaceAllErrors(initialErrors));
                });

                setTitle(formContent.title? formContent.title : "");
                setDesc(formContent.desc? formContent.desc : "");
                setBgColor(formContent.background_color);
                setBgImage(formContent.background_image);
                setHdImage(formContent.header_image);

                if(formContent.background_color === "bg-gray-200" || formContent.background_color === "bg-sky-300" || formContent.background_color === "bg-yellow-200") {
                    copyrightColor.current = "text-black"
                }

                if(body && body.length > 0) {
                    setBody(body);
                }

                if(requiredInfoOld != null) {
                    for(let key in requiredInfo) {
                        if(!requiredInfoOld.hasOwnProperty(key)) {
                            requiredInfoOld[key] = false;
                        }
                    }

                    setRequiredInfo(requiredInfoOld);
                }

                loading.current = true;
            })
            .catch((error) => {
                setNotFound(true);
                loading.current = true;
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();

        let errorFlag = false;

        // validate answer
        for(let id in answerList) {
            if(answerList[id].required) {
                if(answerList[id].type === "multiple") {
                    if(answerList[id].values.every((value: boolean) => value === false)) {
                        errorFlag = true;

                        dispatch(setValue({id:id, value: true}));
                        dispatch(setErrorText({id:id, value: "Please select at least one option"}));
                    }

                    if(answerList[id].otherInfo === "" && answerList[id].values[answerList[id].values.length - 1] === true) {
                        errorFlag = true;

                        dispatch(setValue({id:id, value: true}));
                        dispatch(setErrorText({id:id, value: "Please specify the other"}));
                    }
                }
                else {
                    if(answerList[id].values.length === 0 || answerList[id].values[0] === "" || answerList[id].values[0] === null) {
                        errorFlag = true;

                        dispatch(setValue({id:id, value: true}));
                        dispatch(setErrorText({id:id, value: "The field is required"}));
                    }

                    if(answerList[id].otherInfo === "" && answerList[id].values[0] === "other") {
                        errorFlag = true;

                        dispatch(setValue({id:id, value: true}));
                        dispatch(setErrorText({id:id, value: "Please specify the other"}));
                    }
                }
            }
        }

        // validate contact info
        for(let name in contactInfo) {
            if(name === "declaration") {
                continue;
            }

            if(requiredInfo[name] === true) {
                if(name === "platform") {
                    if(!contactInfo[name].includes(true)) {
                        errorFlag = true;
        
                        dispatch(setValue({id:name, value: true}));
                        dispatch(setErrorText({id:name, value: "Please select at least one platform"}));
                    }
                }
                else {
                    if(contactInfo[name] === "") {
                        errorFlag = true;
        
                        dispatch(setValue({id:name, value: true}));
                        dispatch(setErrorText({id:name, value: "The field is required"}));
                    }
                }
            }
        }

        if(!errorFlag) {
            if(!submitted){
                setSubmitted(true);

                axios.post(`${API_ENDPOINT}/reply`, { 
                    hash: hash,
                    formAns: JSON.stringify(answerList),
                    formContact: JSON.stringify(contactInfo)
                })
                .then(() => {
                    setFinished(true);
                    window.scrollTo(0, 0);
    
                    // set cookie for block end user resubmit within 0.5 days (12 hours)
                    Cookies.set('submitted', '1', { expires: 0.5, path: window.location.pathname });
                })
                .catch(() => {
                    setErrorAlert(true);
                    setSubmitted(false);
                })
            }
        }
        else {
            setErrorAlert(true);
        }
    }

    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
    
        setErrorAlert(false);
    };

    useEffect(() => {
        let newscore = 0;

        for(let id in answerList) {
            if(answerList[id].type === "score" && !Number.isNaN(parseInt(answerList[id].values))) {
                newscore += parseInt(answerList[id].values)
            }
        }

        setScore(newscore)
    },[answerList]);

    const bgStyle = {
        backgroundImage: bgimage ? `url(${bgimage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        width: '100%', 
        height: '100%',
    };

    return (
        <>
            {
                loading.current
                &&
                !notFound
                &&
                !finished
                &&
                <div className={`flex flex-col h-auto min-h-screen py-10 ${bgColor}`} style={bgStyle} >

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        {hdimage !== null && (
                            <img src={hdimage as string} alt="Headerimage" 
                            style={{ width: '50%', height: '200px', marginBottom: '20px' }}/>
                        )}
                    </div>

                    <Card className={"flex flex-col mx-auto p-5 w-4/5 lg:w-2/4"}>
                        <Typography className="text-2xl lg:text-4xl" component="div">
                            <div className="richtext-html" style={{ fontSize: '20px' }} dangerouslySetInnerHTML={{__html: title}}/>
                        </Typography>
                        <Typography component="div">
                            <div className="richtext-html" style={{ fontSize: '20px' }} dangerouslySetInnerHTML={{__html: desc}}/>
                        </Typography>
                    </Card>
                    <form onSubmit={handleSubmit}>
                        {
                            body.map((item, index) => {
                                return (
                                    (
                                        item.type === "question"
                                        &&
                                        <QuestionBox
                                            key={item.id}
                                            id={item.id}
                                            index={index}
                                            type={item.type} 
                                            variant={item.variant}
                                            content={item.content}
                                            options={item?.options} 
                                            images={item?.images}
                                            image={item.image}
                                            required={item.required}
                                            otherOption={item.otherOption}
                                        />
                                    )
                                    ||
                                    (
                                        item.type === "detail"
                                        &&
                                        (item.title || item.desc)
                                        &&
                                        <DetailBox
                                            key={item.id}
                                            index={index}
                                            id={item.id} 
                                            type={item.type}
                                            title={item.title}
                                            desc={item.desc}
                                            image={item.image}
                                        />
                                    )
                                    ||
                                    (
                                        item.type === "image"
                                        &&
                                        item.src
                                        &&
                                        <ImageBox
                                            key={item.id}
                                            index={index}
                                            id={item.id} 
                                            type={item.type}
                                            src={item.src}
                                            height={item.height}
                                            width={item.width}
                                        />
                                    )
                                )
                            })
                        }

                        {score > 0 ?
                            <Card className={"flex flex-col mx-auto mt-5 p-5 w-4/5 lg:w-2/4"}>
                                Total Score : {score}
                            </Card>
                        :
                        ''
                        }

                        <Card className={"flex flex-col mx-auto mt-5 p-5 w-4/5 lg:w-2/4"}>
                            <Typography sx={{ m:1 }} className={"text-2xl lg:text-4xl"}>
                                Contact Information <span className="text-red-600">*</span>
                            </Typography>
                            {
                                requiredInfo.salutation
                                &&
                                <Box sx={{ m:1 }}>
                                    <FormControl 
                                        fullWidth 
                                        sx={{ minWidth: 80, width: 250 }}
                                        error={errorList["salutation"].value}
                                    >
                                        <InputLabel id="select-helper-label">Salutation *</InputLabel>
                                        <Select
                                            labelId="select-helper-label"
                                            label="Salutation"
                                            value={contactInfo.salutation}
                                            onChange={(e) => {
                                                dispatch(update({type:"Salutation", value: e.target.value}));

                                                if(e.target.value === "") {
                                                    dispatch(setValue({id:"salutation", value: true}));
                                                    dispatch(setErrorText({id:"salutation", value: "Please select a salutation"}));
                                                }
                                                else {
                                                    dispatch(setValue({id:"salutation", value: false}));
                                                    dispatch(setErrorText({id:"salutation", value: ""}));
                                                }
                                            }}
                                        >
                                            <MenuItem value=''>
                                                <Typography color="text.secondary" gutterBottom>
                                                    Select
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="Mr.">Mr.</MenuItem>
                                            <MenuItem value="Ms.">Ms.</MenuItem>
                                            <MenuItem value="Dr.">Dr.</MenuItem>
                                        </Select>
                                        <FormHelperText>{errorList["salutation"].errorText}</FormHelperText>
                                    </FormControl>
                                </Box>
                            }
                            {
                                requiredInfo.firstName
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField 
                                        label="First Name *" 
                                        variant="outlined"
                                        value={contactInfo.firstName}
                                        onChange={(e) => {
                                            dispatch(update({type:"First Name", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"firstName", value: true}));
                                                dispatch(setErrorText({id:"firstName", value: "First name is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"firstName", value: false}));
                                                dispatch(setErrorText({id:"firstName", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["firstName"].value}
                                        helperText={errorList["firstName"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.lastName
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField 
                                        label="Last Name *" 
                                        variant="outlined"
                                        value={contactInfo.lastName}
                                        onChange={(e) => {
                                            dispatch(update({type:"Last Name", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"lastName", value: true}));
                                                dispatch(setErrorText({id:"lastName", value: "Last name is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"lastName", value: false}));
                                                dispatch(setErrorText({id:"lastName", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["lastName"].value}
                                        helperText={errorList["lastName"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.title
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField 
                                        label="Title *" 
                                        variant="outlined"
                                        value={contactInfo.title}
                                        onChange={(e) => {
                                            dispatch(update({type:"Title", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"title", value: true}));
                                                dispatch(setErrorText({id:"title", value: "Title is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"title", value: false}));
                                                dispatch(setErrorText({id:"title", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["title"].value}
                                        helperText={errorList["title"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.phoneNum
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField 
                                        label="Phone Number *" 
                                        variant="outlined"
                                        value={contactInfo.phoneNum}
                                        onChange={(e) => {
                                            dispatch(update({type:"Phone Number", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"phoneNum", value: true}));
                                                dispatch(setErrorText({id:"phoneNum", value: "Phone number is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"phoneNum", value: false}));
                                                dispatch(setErrorText({id:"phoneNum", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["phoneNum"].value}
                                        helperText={errorList["phoneNum"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.mobileNum
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField 
                                        label="Mobile Number *" 
                                        variant="outlined"
                                        value={contactInfo.mobileNum}
                                        onChange={(e) => {
                                            dispatch(update({type:"Mobile Number", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"mobileNum", value: true}));
                                                dispatch(setErrorText({id:"mobileNum", value: "Mobile number is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"mobileNum", value: false}));
                                                dispatch(setErrorText({id:"mobileNum", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["mobileNum"].value}
                                        helperText={errorList["mobileNum"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.email
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField
                                        label="Business Email *" 
                                        variant="outlined"
                                        value={contactInfo.email}
                                        onChange={(e) => {
                                            dispatch(update({type:"Email", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"email", value: true}));
                                                dispatch(setErrorText({id:"email", value: "Email is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"email", value: false}));
                                                dispatch(setErrorText({id:"email", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["email"].value}
                                        helperText={errorList["email"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.compName
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField 
                                        label="Company Name *" 
                                        variant="outlined"
                                        value={contactInfo.compName}
                                        onChange={(e) => {
                                            dispatch(update({type:"Company Name", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"compName", value: true}));
                                                dispatch(setErrorText({id:"compName", value: "Company name is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"compName", value: false}));
                                                dispatch(setErrorText({id:"compName", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["compName"].value}
                                        helperText={errorList["compName"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.compAddress
                                &&
                                <Box sx={{ m:1 }}>
                                    <TextField 
                                        label="Company Address *" 
                                        variant="outlined"
                                        value={contactInfo.compAddress}
                                        onChange={(e) => {
                                            dispatch(update({type:"Company Address", value: e.target.value}));

                                            if(e.target.value === "") {
                                                dispatch(setValue({id:"compAddress", value: true}));
                                                dispatch(setErrorText({id:"compAddress", value: "Company address is required"}));
                                            }
                                            else {
                                                dispatch(setValue({id:"compAddress", value: false}));
                                                dispatch(setErrorText({id:"compAddress", value: ""}));
                                            }
                                        }}
                                        className="w-full lg:w-1/3"
                                        error={errorList["compAddress"].value}
                                        helperText={errorList["compAddress"].errorText}
                                    />
                                </Box>
                            }
                            {
                                requiredInfo.compSize
                                &&
                                <Box sx={{ m:1 }}>
                                    <FormControl 
                                        fullWidth 
                                        sx={{ minWidth: 80, width: 250 }}
                                        error={errorList["compSize"].value}
                                    >
                                        <InputLabel id="select-helper-label">Company Size *</InputLabel>
                                        <Select
                                            labelId="select-helper-label"
                                            label="Company Size"
                                            onChange={(e) => {
                                                dispatch(update({type:"Company Size", value: e.target.value}));

                                                if(e.target.value === "") {
                                                    dispatch(setValue({id:"compSize", value: true}));
                                                    dispatch(setErrorText({id:"compSize", value: "Company size is required"}));
                                                }
                                                else {
                                                    dispatch(setValue({id:"compSize", value: false}));
                                                    dispatch(setErrorText({id:"compSize", value: ""}));
                                                }
                                            }}
                                            value={contactInfo.compSize}
                                        >
                                            <MenuItem value=''>
                                                <Typography color="text.secondary" gutterBottom>
                                                    Select
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="< 10">{"< 10"}</MenuItem>
                                            <MenuItem value="10 - 49">{"10 - 49"}</MenuItem>
                                            <MenuItem value="50 - 99">{"50 - 99"}</MenuItem>
                                            <MenuItem value="100 - 499">{"100 - 499"}</MenuItem>
                                            <MenuItem value="500 - 999">{"500 - 999"}</MenuItem>
                                            <MenuItem value="1000 - 4999">{"1000 - 4999"}</MenuItem>
                                            <MenuItem value="≧5000">{"≧5000"}</MenuItem>
                                        </Select>
                                        <FormHelperText>{errorList["compSize"].errorText}</FormHelperText>
                                    </FormControl>
                                </Box>
                            }
                            {
                                requiredInfo.industry
                                &&
                                <Box sx={{ m:1 }}>
                                    <FormControl 
                                        fullWidth 
                                        sx={{ minWidth: 80, width: 250 }}
                                        error={errorList["industry"].value}
                                    >
                                        <InputLabel id="select-helper-label">Industry *</InputLabel>
                                        <Select
                                            labelId="select-helper-label"
                                            label="Industry"
                                            onChange={(e) => {
                                                dispatch(update({type:"Industry", value: e.target.value}));

                                                if(e.target.value === "") {
                                                    dispatch(setValue({id:"industry", value: true}));
                                                    dispatch(setErrorText({id:"industry", value: "Industry is required"}));
                                                }
                                                else {
                                                    dispatch(setValue({id:"industry", value: false}));
                                                    dispatch(setErrorText({id:"industry", value: ""}));
                                                }
                                            }}
                                            value={contactInfo.industry}
                                        >
                                            <MenuItem value=''>
                                                <Typography color="text.secondary" gutterBottom>
                                                    Select
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="Agriculture, forestry and fishing">{"Agriculture, forestry and fishing"}</MenuItem>
                                            <MenuItem value="Mining and quarrying">{"Mining and quarrying"}</MenuItem>
                                            <MenuItem value="Manufacturing">{"Manufacturing"}</MenuItem>
                                            <MenuItem value="Construction">{"Construction"}</MenuItem>
                                            <MenuItem value="Electricity and gas supply">{"Electricity and gas supply"}</MenuItem>
                                            <MenuItem value="Water supply, sewerage, waste management and remediation activities">{"Water supply, sewerage, waste management and remediation activities"}</MenuItem>
                                            <MenuItem value="Import/export, wholesale and retail trades">{"Import/export, wholesale and retail trades"}</MenuItem>
                                            <MenuItem value="Transportation, storage, postal and courier services">{"Transportation, storage, postal and courier services"}</MenuItem>
                                            <MenuItem value="Accommodation and food service activities">{"Accommodation and food service activities"}</MenuItem>
                                            <MenuItem value="Information and communications">{"Information and communications"}</MenuItem>
                                            <MenuItem value="Financial and insurance activities">{"Financial and insurance activities"}</MenuItem>
                                            <MenuItem value="Real estate activities">{"Real estate activities"}</MenuItem>
                                            <MenuItem value="Professional, scientific and technical activities">{"Professional, scientific and technical activities"}</MenuItem>
                                            <MenuItem value="Administrative and support service activities">{"Administrative and support service activities"}</MenuItem>
                                            <MenuItem value="Public administration">{"Public administration"}</MenuItem>
                                            <MenuItem value="Education">{"Education"}</MenuItem>
                                            <MenuItem value="Human health and social work activities">{"Human health and social work activities"}</MenuItem>
                                            <MenuItem value="Arts, entertainment and recreation">{"Arts, entertainment and recreation"}</MenuItem>
                                            <MenuItem value="Other service activities">{"Other service activities"}</MenuItem>
                                        </Select>
                                        <FormHelperText>{errorList["industry"].errorText}</FormHelperText>
                                    </FormControl>
                                </Box>
                            }
                            {
                                requiredInfo.platform
                                &&
                                <Box sx={{ m:1 }}>
                                    <Typography sx={{ fontSize: 16, mt: 2 }}>
                                        The existing platform(s) adopted by your company (You may choose more than one)
                                    </Typography>
                                    <FormControl 
                                        component="fieldset" 
                                        variant="standard" 
                                        error={errorList["platform"].value}
                                    >
                                        <FormGroup sx={{ m: 1}}>
                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                checked={contactInfo.platform[0]} 
                                                onChange={(e) => {
                                                    dispatch(setValue({id:"platform", value: false}));
                                                    dispatch(setErrorText({id:"platform", value: ""}));

                                                    dispatch(update({type:"Platform", index: 0, value: e.target.value}));
                                                }}
                                                />}
                                            label={"Unix"} />

                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                checked={contactInfo.platform[1]} 
                                                onChange={(e) => {
                                                    dispatch(setValue({id:"platform", value: false}));
                                                    dispatch(setErrorText({id:"platform", value: ""}));

                                                    dispatch(update({type:"Platform", index: 1, value: e.target.value}));
                                                }}
                                                />}
                                            label={"Linux"} />

                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                checked={contactInfo.platform[2]} 
                                                onChange={(e) => {
                                                    dispatch(setValue({id:"platform", value: false}));
                                                    dispatch(setErrorText({id:"platform", value: ""}));

                                                    dispatch(update({type:"Platform", index: 2, value: e.target.value}));
                                                }}
                                                />}
                                            label={"Window"} />

                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                checked={contactInfo.platform[3]} 
                                                onChange={(e) => {
                                                    dispatch(setValue({id:"platform", value: false}));
                                                    dispatch(setErrorText({id:"platform", value: ""}));

                                                    dispatch(update({type:"Platform", index: 3, value: e.target.value}));
                                                }}
                                                />}
                                            label={"NetWare"} />

                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                checked={contactInfo.platform[4]} 
                                                onChange={(e) => {
                                                    dispatch(setValue({id:"platform", value: false}));
                                                    dispatch(setErrorText({id:"platform", value: ""}));

                                                    dispatch(update({type:"Platform", index: 4, value: e.target.value}));
                                                }}
                                                />}
                                            label={"Other"} />
                                        </FormGroup>
                                        <FormHelperText>{errorList["platform"].errorText}</FormHelperText>
                                    </FormControl>
                                </Box>
                            }
                            {
                                requiredInfo.declaration
                                &&
                                <Box sx={{ m:1, pt:2 }}>
                                    <Typography sx={{ fontSize: 16 }}>
                                        This data may be used by iCON or selected organizations for future marketing purpose. If you would not prefer to receive information by any means, please tick the box below.
                                    </Typography>
                                    <FormControlLabel 
                                        control={
                                            <Checkbox 
                                                checked={contactInfo.declaration} 
                                                onChange={(e) => {
                                                    dispatch(update({type:"Declaration Checked", value: !contactInfo.declaration}))
                                                }}
                                            />} 
                                        label="I do not agree iCON or selected organizations use this data for future marketing purpose" />
                                </Box>
                            }
                            <Box sx={{ m:1 }} className="flex justify-end">
                                <Button type="submit" variant="contained" color="primary" endIcon={<SendIcon />}>
                                    Submit
                                </Button>
                            </Box>
                        </Card>
                    </form>
                    <Box className="mt-10 mb-5">
                        <Typography className={`${copyrightColor.current} text-center`} sx={{ fontSize: 16 }}>
                            {`©${new Date().getFullYear()-1}-${new Date().getFullYear()} iCON Business Systems Ltd. All Rights Reserved.`}
                        </Typography>
                    </Box>
                    <Box className="flex justify-center">
                        <img 
                            src="/form/icon.png"
                            alt="icon logo"
                            style={{
                                width: 150,
                                height: 50
                            }}
                            loading="lazy"
                            />
                    </Box>
                </div>
            }
            {
                loading.current
                &&
                !notFound
                &&
                finished
                &&
                <div className={`flex flex-col h-auto min-h-screen py-10 ${bgColor}`}>
                    <Card className={"flex flex-col items-center mx-auto p-10 w-4/5 lg:w-2/4"}>
                        <img 
                            src="/form/tick.png" 
                            alt="tick"
                            style={{
                                width: 300,
                                height: 300
                            }}
                            loading="lazy"
                        />
                        <Typography sx={{ fontSize: 32 }}>
                            Successfully submitted! Thank you for your time!
                        </Typography>
                    </Card>
                    <Box className="mt-10 mb-5">
                        <Typography className={`${copyrightColor.current} text-center`} sx={{ fontSize: 16 }}>
                            {`©${new Date().getFullYear()-1}-${new Date().getFullYear()} iCON Business Systems Ltd. All Rights Reserved.`}
                        </Typography>
                    </Box>
                    <Box className="flex justify-center">
                        <img 
                            src="/form/icon.png" 
                            alt="icon logo"
                            style={{
                                width: 150,
                                height: 50
                            }}
                            loading="lazy"
                            />
                    </Box>
                </div>
            }

            {
                loading.current
                &&
                notFound
                &&
                !finished
                &&
                <NotFoundPage></NotFoundPage>
            }

            <Snackbar open={errorAlert} autoHideDuration={3000} onClose={handleSnackbarClose}>
                <Alert variant="filled" severity="error">
                    Can't submit. Please check error message.
                </Alert>
            </Snackbar>
            
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={!loading.current}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    )
}

export default Form;